import { DayPlan } from "@/store/useStore";
import { formatDate } from "./dateFormatter";

// weekHelper.ts
export function getWeekInfo(): string {
    const currentDate = new Date();

    // Get the ISO weekday (Monday = 1, Sunday = 7)
    const jsDay = currentDate.getDay(); // Sunday = 0 ... Saturday = 6
    const isoDay = jsDay === 0 ? 7 : jsDay;

    // Start of ISO week = Monday
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - isoDay + 1);

    // End of ISO week = Sunday
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const formatDate = (date: Date): string => {
        const month = date.toLocaleString('default', { month: 'short' });
        return `${month} ${date.getDate()}`;
    };

    const weekRange = `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}`;
    const weekId = getWeekInfoFromDate(currentDate);

    console.log(`This ISO week is ${weekRange}, Week ${weekId}, ${currentDate.getFullYear()}`);
    return weekId;
}

export function getWeekIdFromDate(date: Date): string {
    // Clone the date to avoid mutating the original
    const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

    // Set to nearest Thursday: ISO week starts on Monday, and week 1 contains the first Thursday
    const day = target.getUTCDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
    const diff = (day <= 4 ? 4 - day : 11 - day);
    target.setUTCDate(target.getUTCDate() + diff);

    // ISO week year
    const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
    const daysBetween = Math.floor((target.getTime() - yearStart.getTime()) / 86400000);

    // Week number
    const week = Math.floor(daysBetween / 7) + 1;

    return `${week}_${target.getUTCFullYear()}`;
}

export function getWeekInfoFromDate(date: Date): string {
    const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = target.getUTCDay() || 7; // Sunday = 7
    target.setUTCDate(target.getUTCDate() + 4 - day); // Shift to Thursday

    const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
    const yearStartDay = yearStart.getUTCDay() || 7;
    const firstThursday = new Date(yearStart);
    firstThursday.setUTCDate(yearStart.getUTCDate() + 4 - yearStartDay);

    const week = Math.floor((target.getTime() - firstThursday.getTime()) / (7 * 86400000)) + 1;
    return `${week}_${target.getUTCFullYear()}`;
}

export function getDateRangeFromWeekId(weekId: string): { startDate: Date; endDate: Date } {
  const [weekStr, yearStr] = weekId.split("_");
  const week = parseInt(weekStr, 10);
  const year = parseInt(yearStr, 10);

  // Step 1: Find the Thursday of the first ISO week of that year
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const yearStartDay = yearStart.getUTCDay() || 7;  // Sunday -> 7
  const firstThursday = new Date(yearStart);
  firstThursday.setUTCDate(yearStart.getUTCDate() + 4 - yearStartDay);

  // Step 2: From that Thursday, get the Thursday of the target week
  const targetThursday = new Date(firstThursday);
  targetThursday.setUTCDate(firstThursday.getUTCDate() + (week - 1) * 7);

  // Step 3: ISO weeks start on Monday and end on Sunday
  const startDate = new Date(targetThursday);
  startDate.setUTCDate(targetThursday.getUTCDate() - 3); // Monday

  const endDate = new Date(targetThursday);
  endDate.setUTCDate(targetThursday.getUTCDate() + 3);   // Sunday

  return { startDate, endDate };
}

export function createEmptyWeek(startDate: Date = new Date()): DayPlan[] {
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() - date.getDay() + i + 1);
    return {
      date,
      meals: {
        breakfast: undefined,
        lunch: undefined,
        dinner: undefined,
        snacks: []
      }
    };
  });
}

export function normalizeWeek(data: any, weekId: string): DayPlan[] {
  if (!Array.isArray(data) || data.length !== 7) {
    const { startDate } = getDateRangeFromWeekId(weekId);
    return createEmptyWeek(startDate);
  }

  return data.map((item, i) => {
    const date = item?.date ? formatDate(item.date) : createEmptyWeek()[i].date;
    const meals = item?.meals || {};
    return {
      date,
      meals: {
        breakfast: meals.breakfast ?? undefined,
        lunch: meals.lunch ?? undefined,
        dinner: meals.dinner ?? undefined,
        snacks: Array.isArray(meals.snacks) ? meals.snacks : []
      }
    };
  });
}